import { useEffect, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { db } from '@/data/supabase';

const TEMPLATES = [
  {
    label: 'Service complete',
    subject: 'Service complete — your pool is sparkling',
    body: 'Hi {name}, your pool service is complete today. All chemistry is balanced and ready to swim. Let us know if you have any questions!',
  },
  {
    label: 'Issue found',
    subject: 'Pool issue found at your home today',
    body: 'Hi {name}, while servicing your pool today I noticed an issue that needs your attention. Could we set up a quick call to discuss next steps?',
  },
  {
    label: 'Payment reminder',
    subject: 'Friendly reminder: invoice due',
    body: 'Hi {name}, just a friendly reminder your invoice is past due. You can pay online or reach out with any questions. Thanks!',
  },
  {
    label: 'Heads up — running late',
    subject: 'Quick heads up',
    body: 'Hi {name}, just letting you know I am running a bit behind today and should be at your home within the next hour. Thanks for your patience!',
  },
];

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function SendMessageModal({ data }) {
  const { custById, sendMessage, closeModal, cloudMode } = useApp();
  const c = data?.custId ? custById(data.custId) : null;

  const [channel, setChannel] = useState(c?.phone ? 'sms' : 'email');
  const [to, setTo]           = useState(channel === 'email' ? (c?.email || '') : (c?.phone || ''));
  const [subject, setSubject] = useState('');
  const [body, setBody]       = useState('');
  const [busy, setBusy]       = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  // Update "to" when channel switches
  useEffect(() => {
    if (!c) return;
    setTo(channel === 'email' ? (c.email || '') : (c.phone || ''));
  }, [channel, c]);

  // Load history for this customer
  useEffect(() => {
    if (!c || !cloudMode) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await db.messages.listForCustomer(c.id);
      if (!cancelled) {
        setHistory(res?.data || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [c, cloudMode]);

  const applyTemplate = (tpl) => {
    const name = c?.name?.split(',')?.[1]?.trim() || c?.name?.split(' ')?.[0] || 'there';
    setSubject(tpl.subject);
    setBody(tpl.body.replaceAll('{name}', name));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!to || !body) return;
    setBusy(true);
    const res = await sendMessage({
      to,
      channel,
      subject: channel === 'email' ? subject : undefined,
      body,
      customerId: c?.id,
    });
    setBusy(false);
    // Add the row to history regardless of success — failed attempts
    // are logged too and showing them gives the user a clear error trail.
    if (res.message) setHistory(prev => [res.message, ...prev]);
    if (res.ok) {
      setBody('');
      setSubject('');
    }
  };

  if (!c) {
    return (
      <div className="modal">
        <div className="modal-head">
          <span className="modal-title">Send Message</span>
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>✕</button>
        </div>
        <div className="modal-body" style={{ padding: 20 }}>
          <div style={{ color: 'var(--t2)' }}>No customer selected.</div>
        </div>
      </div>
    );
  }

  const canSend = !!to && !!body && !busy && (channel === 'sms' || subject);

  return (
    <div className="modal" style={{ width: 'min(640px, 95vw)' }}>
      <div className="modal-head">
        <span className="modal-title">Message {c.name}</span>
        <button className="btn btn-ghost btn-sm" onClick={closeModal}>✕</button>
      </div>
      <div className="modal-body">
        {/* Channel toggle */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <button
            type="button"
            className={`btn btn-sm ${channel === 'sms' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setChannel('sms')}
            disabled={!c.phone}
            title={c.phone ? '' : 'No phone on file'}
          >
            📱 SMS
          </button>
          <button
            type="button"
            className={`btn btn-sm ${channel === 'email' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setChannel('email')}
            disabled={!c.email}
            title={c.email ? '' : 'No email on file'}
          >
            ✉️ Email
          </button>
        </div>

        {/* Templates */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--t3)', marginBottom: 6 }}>
            Templates
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TEMPLATES.map(tpl => (
              <button
                key={tpl.label}
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => applyTemplate(tpl)}
              >
                {tpl.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSend}>
          <div className="field">
            <label>To</label>
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder={channel === 'email' ? 'name@email.com' : '+15555550100'}
              required
            />
          </div>

          {channel === 'email' && (
            <div className="field">
              <label>Subject</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject line"
                required
              />
            </div>
          )}

          <div className="field">
            <label>Message {channel === 'sms' && <span style={{ color: 'var(--t3)', fontWeight: 400 }}>({body.length} chars)</span>}</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your message…"
              rows={5}
              required
            />
          </div>

          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={closeModal}>Close</button>
            <button type="submit" className="btn btn-primary" disabled={!canSend}>
              {busy ? 'Sending…' : `Send ${channel === 'email' ? 'Email' : 'SMS'}`}
            </button>
          </div>
        </form>

        {/* History */}
        <div style={{ marginTop: 18, borderTop: '1px solid var(--b1)', paddingTop: 12 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--t3)', marginBottom: 8 }}>
            Message History
          </div>
          {loading && <div style={{ fontSize: 12, color: 'var(--t3)' }}>Loading…</div>}
          {!loading && history.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--t3)' }}>No messages yet</div>
          )}
          {history.slice(0, 10).map(m => (
            <div key={m.id} style={{
              padding: '8px 10px',
              borderRadius: 6,
              background: 'var(--s2)',
              marginBottom: 6,
              fontSize: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                <div style={{ fontWeight: 600 }}>
                  {m.channel === 'email' ? '✉️' : '📱'} {m.to_addr}
                  <span style={{
                    marginLeft: 8,
                    fontSize: 10,
                    padding: '2px 6px',
                    borderRadius: 4,
                    background: m.status === 'sent' ? 'var(--teal-t)' : m.status === 'failed' ? 'var(--rose-t)' : 'var(--s3)',
                    color: m.status === 'sent' ? 'var(--teal)' : m.status === 'failed' ? 'var(--rose)' : 'var(--t2)',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    letterSpacing: '.05em',
                  }}>
                    {m.status}
                  </span>
                </div>
                <div style={{ color: 'var(--t3)', fontSize: 11 }}>{fmtTime(m.created_at)}</div>
              </div>
              {m.subject && <div style={{ fontWeight: 500, color: 'var(--t1)' }}>{m.subject}</div>}
              <div style={{ color: 'var(--t2)', whiteSpace: 'pre-wrap' }}>{m.body}</div>
              {m.error && <div style={{ color: 'var(--rose)', fontSize: 11, marginTop: 4 }}>Error: {m.error}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
